import * as React from "react";
import Box from "@mui/material/Box";
import { Text } from "@fluentui/react";
import { Icon } from "@fluentui/react/lib/Icon";
import { CheckCircle, ExternalLink, History, XCircle } from "lucide-react";
import type { Email, EmailShipment, LTLShipment, StatusHistoryEntry } from "@src/types/api";
import {
  getLTLOriginDestination,
  getLTLWeight,
  getFTLOriginDestination,
  getFTLWeight,
  formatNumber,
} from "@taskpane/utils/shipmentUtils";
import {
  interactionLeadLabelForShipment,
  interactionLeadLocalPart,
} from "@taskpane/utils/interactionLeadDisplay";
import { shipmentUiBucketFromUnifiedDoc } from "@shared/utils/shipmentUiBucket";
import { resolveShipmentStatus, resolveStatusHistoryEntry } from "@shared/status/statusResolver";
import type { StatusDefinition } from "@shared/status/types";
import { formatEmailSentTime } from "@taskpane/utils/emailSentTime";
import { StatusPill } from "../StatusPill";

function getShipmentDisplay(shipment: EmailShipment): {
  origin: { line1: string; line2: string };
  destination: { line1: string; line2: string };
  weightLbs: number;
  mode: "LTL" | "FTL";
  pallets: number;
} {
  const bucket = shipmentUiBucketFromUnifiedDoc(shipment);

  if (bucket === "FTL") {
    const s = shipment as import("@src/types/api").FTLShipment;
    const { origin, destination } = getFTLOriginDestination(s);

    return { origin, destination, weightLbs: getFTLWeight(s), mode: "FTL", pallets: 0 };
  }
  const s = shipment as LTLShipment;
  const { origin, destination } = getLTLOriginDestination(s);
  const pallets =
    typeof s.requiredFields?.totalHandlingUnitCount === "number"
      ? s.requiredFields.totalHandlingUnitCount
      : 0;

  return { origin, destination, weightLbs: getLTLWeight(s), mode: "LTL", pallets };
}

export interface GlobalShipmentRowProps {
  email: Email;
  shipment: EmailShipment;
  statuses: StatusDefinition[];
  onNavigate: () => void;
}

function formatStatusHistoryTime(value: string): string {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value || "Unknown time";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

const styles = {
  card: {
    background: "#fff",
    border: "1px solid #edebe9",
    borderRadius: "10px",
    padding: "10px 12px",
    marginBottom: "10px",
    cursor: "default",
    width: "100%",
    boxSizing: "border-box",
    transition: "background-color 120ms ease",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
  } as React.CSSProperties,
  routeRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "8px",
    marginBottom: "4px",
  } as React.CSSProperties,
  routeLine: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flex: 1,
    minWidth: 0,
  } as React.CSSProperties,
  interactionLeadPill: {
    borderRadius: 999,
    padding: "3px 10px",
    fontSize: "x-small",
    fontWeight: 600,
    lineHeight: 1.4,
    whiteSpace: "nowrap" as const,
    textAlign: "left" as const,
    display: "inline-flex",
    flexDirection: "row" as const,
    alignItems: "center",
    gap: "6px",
    flexShrink: 0,
    maxWidth: "42%",
    minWidth: 0,
    overflow: "hidden",
    background: "#e8e8e8",
    color: "#605e5c",
    border: "1px solid #d2d0ce",
  } as React.CSSProperties,
  interactionLeadName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
  } as React.CSSProperties,
  interactionLeadVerifyIcon: {
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
    lineHeight: 0,
  } as React.CSSProperties,
  companyLine: {
    fontSize: "11px",
    color: "#605e5c",
    lineHeight: "16px",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    marginBottom: "2px",
  } as React.CSSProperties,
  navButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "26px",
    height: "26px",
    border: "1px solid #d2d0ce",
    borderRadius: "50%",
    background: "#ffffff",
    cursor: "pointer",
    flexShrink: 0,
    color: "#605e5c",
    lineHeight: 0,
    "&:hover": {
      background: "#f5f7f9",
      color: "#323130",
      borderColor: "#a19f9d",
    },
  } as React.CSSProperties,
  cityText: {
    fontSize: "13px",
    fontWeight: "600" as const,
    color: "#323130",
    whiteSpace: "nowrap" as const,
    overflow: "hidden" as const,
    textOverflow: "ellipsis" as const,
  },
  arrowIcon: {
    fontSize: "10px",
    color: "#a19f9d",
    flexShrink: 0,
  },
  zipText: {
    fontSize: "11px",
    color: "#605e5c",
    marginBottom: "3px",
  },
  metaText: {
    fontSize: "11px",
    color: "#605e5c",
  },
  sentTime: {
    fontSize: "10px",
    color: "#605e5c",
    lineHeight: "14px",
    marginTop: "2px",
  } as React.CSSProperties,
  bottomRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "6px",
    gap: "8px",
    minHeight: "22px",
  } as React.CSSProperties,
  subjectLine: {
    fontSize: "11px",
    color: "#605e5c",
    lineHeight: "16px",
    fontStyle: "italic",
    whiteSpace: "nowrap" as const,
    overflow: "hidden" as const,
    textOverflow: "ellipsis" as const,
    flex: 1,
    minWidth: 0,
  } as React.CSSProperties,
  statusActions: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flexShrink: 0,
  } as React.CSSProperties,
  statusHistoryButton: {
    width: 26,
    height: 26,
    border: "1px solid #d2d0ce",
    borderRadius: "50%",
    background: "#ffffff",
    color: "#605e5c",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    cursor: "pointer",
    lineHeight: 0,
    "&:hover": {
      background: "#f5f7f9",
      color: "#323130",
      borderColor: "#a19f9d",
    },
    "&:focus-visible": {
      outline: "2px solid #0f6cbd",
      outlineOffset: 2,
    },
  },
  statusHistoryPanel: {
    marginTop: "8px",
    padding: "10px",
    border: "1px solid #edebe9",
    borderRadius: "6px",
    background: "#ffffff",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
    maxHeight: "220px",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  } as React.CSSProperties,
  statusHistoryTitle: {
    fontSize: "11px",
    fontWeight: 700,
    color: "#323130",
    lineHeight: "14px",
    marginBottom: "8px",
  } as React.CSSProperties,
  statusHistoryList: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    overflowY: "auto",
    paddingRight: "4px",
  } as React.CSSProperties,
  statusHistoryRow: {
    display: "grid",
    gridTemplateColumns: "minmax(92px, max-content) minmax(0, 1fr)",
    gap: "2px 8px",
    alignItems: "center",
  } as React.CSSProperties,
  statusHistoryStatusCell: {
    gridRow: "1 / span 2",
  } as React.CSSProperties,
  statusHistoryUserText: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#323130",
    lineHeight: "14px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  } as React.CSSProperties,
  statusHistoryTimeText: {
    fontSize: "10px",
    color: "#605e5c",
    lineHeight: "14px",
  } as React.CSSProperties,
  statusHistoryEmptyText: {
    fontSize: "11px",
    color: "#605e5c",
    lineHeight: "16px",
    fontStyle: "italic",
  } as React.CSSProperties,
};

function displayInteractingUser(raw: string | undefined): string {
  const t = typeof raw === "string" ? raw.trim() : "";

  if (!t) return "Unknown user";

  return interactionLeadLocalPart(t) ?? t;
}

export default function GlobalShipmentRow({
  email,
  shipment,
  statuses,
  onNavigate,
}: GlobalShipmentRowProps) {
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const { origin, destination, weightLbs, mode, pallets } = getShipmentDisplay(shipment);
  const statusHistory = (shipment as { statusHistory?: StatusHistoryEntry[] }).statusHistory;
  const verifiedSender = email.organization?.senderCustomerVerified;
  const senderLabel = interactionLeadLocalPart(email.sender);
  const lastInteractingRaw = interactionLeadLabelForShipment(undefined, statusHistory);
  const lastInteractingLabel = lastInteractingRaw
    ? (interactionLeadLocalPart(lastInteractingRaw) ?? lastInteractingRaw)
    : undefined;
  const nameForPill = lastInteractingLabel ?? senderLabel;
  const showVerifyIcons = typeof verifiedSender === "boolean";
  const showInteractionPill = Boolean(nameForPill) || showVerifyIcons;
  const subject = email.subject ?? "(no subject)";
  const sentLabel = formatEmailSentTime(email);
  const loadStatus = (shipment as { loadStatus?: string }).loadStatus;
  const resolvedStatus = resolveShipmentStatus(
    {
      loadStatus,
      classification: shipment.classification,
      bolClassification: shipment.bolClassification,
    },
    statuses
  );
  const metaParts: string[] = [mode];

  if (weightLbs > 0) metaParts.push(`${formatNumber(weightLbs)} lbs`);
  if (pallets > 0) metaParts.push(`${pallets} pallet${pallets !== 1 ? "s" : ""}`);

  return (
    <Box sx={styles.card}>
      <div style={styles.routeRow}>
        <div style={styles.routeLine}>
          <Text styles={{ root: styles.cityText }}>{origin.line1}</Text>
          <Icon iconName="Forward" styles={{ root: styles.arrowIcon }} />
          <Text styles={{ root: styles.cityText }}>{destination.line1}</Text>
        </div>
        {showInteractionPill ? (
          <div style={styles.interactionLeadPill}>
            {nameForPill ? <span style={styles.interactionLeadName}>{nameForPill}</span> : null}
            {showVerifyIcons ? (
              <span style={styles.interactionLeadVerifyIcon}>
                {verifiedSender ? (
                  <CheckCircle size={16} color="#3b82f6" aria-label="Verified customer" />
                ) : (
                  <XCircle size={16} color="#ef4444" aria-label="Unverified customer" />
                )}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {email.companyName ? <div style={styles.companyLine}>{email.companyName}</div> : null}

      {(origin.line2 || destination.line2) && (
        <div style={{ marginBottom: "3px" }}>
          <Text styles={{ root: styles.zipText }}>
            {origin.line2 || "—"} → {destination.line2 || "—"}
          </Text>
        </div>
      )}

      <Text styles={{ root: styles.metaText }}>{metaParts.join(" • ")}</Text>
      {sentLabel ? (
        <div
          style={styles.sentTime}
          title={email.sentDateTime ? `Sent ${email.sentDateTime}` : undefined}
        >
          {sentLabel}
        </div>
      ) : null}

      <div style={styles.bottomRow}>
        <div style={styles.subjectLine} title={subject}>
          {subject}
        </div>
        <div onClick={(e) => e.stopPropagation()} style={styles.statusActions}>
          <Box
            component="button"
            type="button"
            className="global-shipment-status-history-button"
            aria-label="Show status history"
            aria-expanded={historyOpen}
            onClick={() => setHistoryOpen((open) => !open)}
            sx={styles.statusHistoryButton}
          >
            <History size={15} aria-hidden="true" />
          </Box>
          {resolvedStatus.status ? <StatusPill status={resolvedStatus} /> : <span />}
          <button
            style={styles.navButton}
            title="Open email in Outlook"
            aria-label="Open email in Outlook"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate();
            }}
          >
            <ExternalLink size={14} />
          </button>
        </div>
      </div>
      {historyOpen ? (
        <div onClick={(e) => e.stopPropagation()} style={styles.statusHistoryPanel}>
          <div style={styles.statusHistoryTitle}>Status History</div>
          {Array.isArray(statusHistory) &&
          statusHistory.filter((entry) => entry?.status).length > 0 ? (
            <div style={styles.statusHistoryList}>
              {statusHistory
                .filter((entry) => entry?.status)
                .map((entry, index) => {
                  const resolvedEntry = resolveStatusHistoryEntry(
                    entry.status,
                    {
                      classification: shipment.classification,
                      bolClassification: shipment.bolClassification,
                    },
                    statuses
                  );

                  return (
                    <div
                      key={`${entry.status}-${entry.updateTime}-${index}`}
                      style={styles.statusHistoryRow}
                    >
                      <div style={styles.statusHistoryStatusCell}>
                        <StatusPill status={resolvedEntry} />
                      </div>
                      <div style={styles.statusHistoryUserText}>
                        {displayInteractingUser(entry.interactingUser)}
                      </div>
                      <div style={styles.statusHistoryTimeText}>
                        {formatStatusHistoryTime(entry.updateTime)}
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div style={styles.statusHistoryEmptyText}>No status history recorded.</div>
          )}
        </div>
      ) : null}
    </Box>
  );
}
